import React, { useRef, useState, useEffect } from 'react';
import { HeartIcon as HeartSolid, MapPinIcon, ClockIcon } from '@heroicons/react/24/solid';
import { HeartIcon as HeartOutline, ArrowPathIcon } from '@heroicons/react/24/outline';
import { getCategoryColor } from '../../utils/categoryColors';
import { getEventPlaceholder } from '../../utils/eventPlaceholder';

const EventCard = ({
  event,
  index,
  formatEventDate,
  normalizeKey,
  savedEventIds,
  onOpenModal,
  onToggleSave,
  t,
  i18nLanguage,
}) => {
  const { primary, light } = getCategoryColor(event.category);
  const descRef = useRef(null);
  const [isClamped, setIsClamped] = useState(false);

  useEffect(() => {
    const el = descRef.current;
    if (el) setIsClamped(el.scrollHeight > el.clientHeight + 1);
  }, [event.description]);

  return (
    <div
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpenModal(event); } }}
      onClick={() => onOpenModal(event)}
      className="flex flex-col sm:flex-row bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-md hover:shadow-xl hover:-translate-y-0.5 active:shadow-md active:scale-[0.99] cursor-pointer group transition-all duration-300"
      style={{ borderLeft: `4px solid ${primary}` }}
    >
      <div className="sm:w-52 relative overflow-hidden bg-brand-dark">
        <div className="aspect-[16/10] sm:aspect-auto sm:h-full w-full min-h-[160px] sm:min-h-[180px] md:min-h-[200px] max-h-[200px] sm:max-h-[220px] md:max-h-[240px]">
          <img
            src={event.photoUrl || getEventPlaceholder(event.category)}
            className="w-full h-full object-contain object-center"
            alt={event.title || ''}
            loading={index < 2 ? 'eager' : 'lazy'}
            decoding="async"
          />
        </div>
        <div className="absolute top-3 left-3 bg-white/95 backdrop-blur-sm rounded-xl px-2.5 py-1.5 text-center shadow-md">
          <div className="text-[10px] font-bold uppercase text-gray-500 leading-none">
            {new Date(event.start).toLocaleDateString(i18nLanguage, { month: 'short' })}
          </div>
          <div className="text-lg font-black text-gray-900 leading-tight">
            {new Date(event.start).getDate()}
          </div>
        </div>
        <button
          onClick={(e) => onToggleSave(e, event._id)}
          className="absolute top-3 right-3 p-2 bg-white/90 rounded-full shadow hover:bg-white transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
        >
          {savedEventIds.includes(event._id)
            ? <HeartSolid className="h-5 w-5 text-red-500" />
            : <HeartOutline className="h-5 w-5 text-gray-400" />
          }
        </button>
      </div>
      <div className="p-4 md:p-5 flex-1 min-w-0 flex flex-col">
        <div className="flex items-center gap-1.5 flex-wrap mb-1.5">
          <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded" style={{ backgroundColor: light, color: primary }}>
            {t(`events.event-management.event-type.${normalizeKey(event.category)}`)}
          </span>
          {event.recurrence?.type && event.recurrence.type !== 'none' && (
            <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded bg-slate-100 text-slate-500 inline-flex items-center gap-0.5">
              <ArrowPathIcon className="h-3 w-3" />
              {t(`events.event-management.recurrence.${event.recurrence.type}`)}
            </span>
          )}
        </div>
        <h3 className="text-base md:text-lg font-bold text-gray-900 leading-snug line-clamp-2">{event.title}</h3>
        <div className="mt-1.5 flex items-start gap-1.5">
          <MapPinIcon className="h-3.5 w-3.5 text-gray-400 flex-shrink-0 mt-0.5" />
          <div className="min-w-0">
            <p className="text-sm text-gray-700 font-medium truncate">{event.provider?.businessName}</p>
            <p className="text-xs text-gray-400 truncate">
              {event.location?.useProvider === false
                ? (event.location.name || event.location.address || event.provider?.location?.address)
                : (event.location?.address || event.provider?.location?.address)}
            </p>
          </div>
        </div>
        <p className="text-sm font-bold text-[#f05537] mt-1.5 flex items-center gap-1">
          <ClockIcon className="h-3.5 w-3.5 flex-shrink-0" />
          {formatEventDate(event.start, event.end)}
        </p>
        {event.description && (
          <div className="mt-2 flex-1">
            <p ref={descRef} className="text-[13px] text-gray-500 leading-relaxed line-clamp-2 sm:line-clamp-3">
              {event.description}
            </p>
            {isClamped && (
              <button
                onClick={(e) => { e.stopPropagation(); onOpenModal(event); }}
                className="text-xs font-semibold text-blue-500 hover:text-blue-700 mt-1 min-h-[44px] flex items-center transition-colors"
              >
                {t('hero.read')}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default EventCard;
